import React, { useCallback, useContext, useEffect, useState } from 'react';
import { LineChartOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { Frequency } from '../../../models';
import { UserContext } from '../../contexts';
import { FrequencyEditPage } from './frequency-edit-page';
import { EntityChangeLog } from '../../reports';

export function FrequenciesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [frequencies, setFrequencies] = useState<Frequency[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalFrequency, setEditModalFrequency] = useState<Frequency>();
  const [canEdit, setCanEdit] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'frequencies.edit'));
  }, [user]);

  const loadFrequencies = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`frequencies`)
      .then((response: any) => {
        const responseFrequencies: Frequency[] = response.data;
        const responseData = responseFrequencies.map(frequency => {
          return {
            key: frequency.id,
            ...frequency,
          };
        });
        setFrequencies(responseData);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadFrequencies();
  }, [loadFrequencies]);

  const onRefreshClick = () => {
    loadFrequencies();
  };

  const onFrequencyEditSaved = () => {
    closeEditModal();
    loadFrequencies();
  };

  const openEditModal = () => {
    setEditModalFrequency(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByFrequency = (frequency: Frequency) => {
    setEditModalFrequency(frequency);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalFrequency(undefined);
  };

  const deleteFrequency = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`frequencies/${id}`)
      .then(() => {
        setLoading(false);
        loadFrequencies();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmdeleteFrequency = (id: string) => {
    const onOk = () => deleteFrequency(id);
    Modal.confirm({
      title: 'Frekans sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Frekans silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const frequency = frequencies?.find(m => m.id === id);
      if (frequency) {
        openEditModalByFrequency(frequency);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmdeleteFrequency(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'En düşük',
      dataIndex: 'lowest',
      key: 'lowest',
    },
    {
      title: 'En yüksek',
      dataIndex: 'highest',
      key: 'highest',
    },
    {
      title: 'Öncelik puanı',
      dataIndex: 'priorityScore',
      key: 'priorityScore',
    },
    {
      title: '',
      key: 'action',
      render: (_: any, frequency: Frequency) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(frequency.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <LineChartOutlined /> Frekanslar
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" icon={<PlusOutlined />} onClick={openEditModal} type="primary">
                Yeni frekans
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={frequencies} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'Frequency'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal title="Frekans" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <FrequencyEditPage frequency={editModalFrequency} onSaved={onFrequencyEditSaved} onCancel={closeEditModal} />
      </Modal>
    </>
  );
}
